import React, { useState, useEffect, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { fbFunctions } from '../../../firebase/firebase';

const ChatSupportWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');

  const chatContainerRef = useRef(null);

  // Close chat when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (chatContainerRef.current && !chatContainerRef.current.contains(event.target) &&
          !event.target.classList.contains('chat-toggle-button')) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    
    // Clear error when typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: '',
      });
    }
    
    // Clear submit error and success
    if (submitError) {
      setSubmitError('');
    }
    if (submitSuccess) {
      setSubmitSuccess('');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Validate name
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    // Validate email
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    // Validate message
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError('');
    setSubmitSuccess('');
    
    try {
      // Call Firebase Cloud Function
      const logChatSupport = httpsCallable(fbFunctions, 'log_chat_support_req');
      const result = await logChatSupport({
        name: formData.name.trim(),
        email: formData.email.trim(),
        message: formData.message.trim(),
      });
      
      // Check if the function returned success (case-insensitive and null-safe)
      if (result.data?.status?.toLowerCase() === 'success') {
        setSubmitSuccess(result.data.message || 'Message sent successfully!');
        setIsSubmitted(true);
        
        // Reset form after 3 seconds
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            message: '',
          });
          setIsSubmitted(false);
          setIsOpen(false);
          setSubmitSuccess('');
        }, 3000);
      } else {
        // Handle function error
        setSubmitError(result.data?.message || 'Failed to submit message. Please try again.');
      }
      
    } catch (error) {
      console.error("Error submitting feedback:", error);
      
      // Handle different types of errors
      if (error.code === 'functions/unauthenticated') {
        setSubmitError('Authentication required. Please refresh the page and try again.');
      } else if (error.code === 'functions/permission-denied') {
        setSubmitError('Permission denied. Please check your access rights.');
      } else if (error.code === 'functions/internal') {
        setSubmitError('Server error. Please try again later.');
      } else {
        setSubmitError('Failed to submit message. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="chat-support-widget" style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 1000 }}>
      {/* Chat button */}
      <button
        className="chat-toggle-button"
        onClick={toggleChat}
        style={{
          width: '60px',
          height: '60px',
          borderRadius: '50%',
          backgroundColor: '#111d4a',
          color: 'white',
          border: 'none',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          outline: 'none',
          transition: 'all 0.2s ease',
        }}
        aria-label="Open chat support"
      >
        {isOpen ? (
          // Close icon
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        ) : (
          // Chat icon
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </button>

      {/* Chat container */}
      {isOpen && (
        <div
          ref={chatContainerRef}
          style={{
            position: 'absolute',
            bottom: '75px',
            right: '0',
            width: '350px',
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 5px 25px rgba(0, 0, 0, 0.2)',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            animation: 'slideIn 0.3s ease',
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '16px',
              backgroundColor: '#111d4a',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <div style={{ marginRight: '12px' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 11.5C21.0034 12.8199 20.6951 14.1219 20.1 15.3C19.3944 16.7118 18.3098 17.8992 16.9674 18.7293C15.6251 19.5594 14.0782 19.9994 12.5 20C11.1801 20.0035 9.87812 19.6951 8.7 19.1L3 21L4.9 15.3C4.30493 14.1219 3.99656 12.8199 4 11.5C4.00061 9.92179 4.44061 8.37488 5.27072 7.03258C6.10083 5.69028 7.28825 4.6056 8.7 3.90003C9.87812 3.30496 11.1801 2.99659 12.5 3.00003H13C15.0843 3.11502 17.053 3.99479 18.5291 5.47089C20.0052 6.94699 20.885 8.91568 21 11V11.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <h3 style={{ margin: '0', fontSize: '18px', fontWeight: '600', color: '#FFF' }}>Chat with us</h3>
              <p style={{ margin: '4px 0 0', fontSize: '14px', opacity: '0.8' }}>
                We typically reply within an hour
              </p>
            </div>
          </div>

          {/* Content */}
          <div style={{ padding: '20px' }}>
            {isSubmitted ? (
              <div
                style={{
                  textAlign: 'center',
                  padding: '20px 0',
                  color: '#16a34a'
                }}
              >
                <svg
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ marginBottom: '16px' }}
                >
                  <path
                    d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18455 2.99721 7.13631 4.39828 5.49706C5.79935 3.85781 7.69279 2.71537 9.79619 2.24013C11.8996 1.7649 14.1003 1.98232 16.07 2.85999"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M22 4L12 14.01L9 11.01"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <h4 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: '600' }}>
                  Thanks for reaching out!
                </h4>
                {submitSuccess && (
                  <p style={{ margin: '0 0 8px', color: '#16a34a', fontSize: '15px', fontWeight: '500' }}>
                    {submitSuccess}
                  </p>
                )}
                <p style={{ margin: '0', color: '#4b5563', fontSize: '15px' }}>
                  We'll get back to you as soon as possible.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {/* Show success message if any */}
                {submitSuccess && (
                  <div style={{
                    marginBottom: '16px',
                    padding: '12px',
                    backgroundColor: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    borderRadius: '6px',
                    color: '#16a34a',
                    fontSize: '14px'
                  }}>
                    {submitSuccess}
                  </div>
                )}
                
                {/* Show general error if any */}
                {submitError && (
                  <div style={{
                    marginBottom: '16px',
                    padding: '12px',
                    backgroundColor: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '6px',
                    color: '#dc2626',
                    fontSize: '14px'
                  }}>
                    {submitError}
                  </div>
                )}

                <div style={{ marginBottom: '16px' }}>
                  <label
                    htmlFor="name"
                    style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151'
                    }}
                  >
                    Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Your name"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: '15px',
                      borderRadius: '6px',
                      border: errors.name ? '1px solid #ef4444' : '1px solid #d1d5db',
                      backgroundColor: '#fff',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      boxSizing: 'border-box'
                    }}
                  />
                  {errors.name && (
                    <p style={{
                      margin: '4px 0 0',
                      fontSize: '13px',
                      color: '#ef4444'
                    }}>
                      {errors.name}
                    </p>
                  )}
                </div>
                
                <div style={{ marginBottom: '16px' }}>
                  <label
                    htmlFor="email"
                    style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151'
                    }}
                  >
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your.email@example.com"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: '15px',
                      borderRadius: '6px',
                      border: errors.email ? '1px solid #ef4444' : '1px solid #d1d5db',
                      backgroundColor: '#fff',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      boxSizing: 'border-box'
                    }}
                  />
                  {errors.email && (
                    <p style={{
                      margin: '4px 0 0',
                      fontSize: '13px',
                      color: '#ef4444'
                    }}>
                      {errors.email}
                    </p>
                  )}
                </div>
                
                <div style={{ marginBottom: '16px' }}>
                  <label
                    htmlFor="message"
                    style={{
                      display: 'block',
                      marginBottom: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#374151'
                    }}
                  >
                    How can we help? *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Share your feedback or questions..."
                    rows="4"
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: '15px',
                      borderRadius: '6px',
                      border: errors.message ? '1px solid #ef4444' : '1px solid #d1d5db',
                      backgroundColor: '#fff',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      resize: 'vertical',
                      boxSizing: 'border-box'
                    }}
                  />
                  {errors.message && (
                    <p style={{
                      margin: '4px 0 0',
                      fontSize: '13px',
                      color: '#ef4444'
                    }}>
                      {errors.message}
                    </p>
                  )}
                </div>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#111d4a',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '15px',
                    fontWeight: '500',
                    cursor: isSubmitting ? 'default' : 'pointer',
                    opacity: isSubmitting ? 0.7 : 1,
                    transition: 'background-color 0.2s',
                    outline: 'none',
                  }}
                  onMouseOver={(e) => {
                    if (!isSubmitting) {
                      e.target.style.backgroundColor = '#1e2d64';
                    }
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = '#111d4a';
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          marginRight: '8px',
                          animation: 'spin 1s linear infinite'
                        }}
                      >
                        <style>{`
                          @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                          }
                        `}</style>
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                          strokeDasharray="30 60"
                        />
                      </svg>
                      Sending...
                    </>
                  ) : (
                    'Send message'
                  )}
                </button>
              </form>
            )}
          </div>
          
          {/* Footer */}
          <div
            style={{
              borderTop: '1px solid #e5e7eb',
              padding: '12px 16px',
              fontSize: '13px',
              color: '#6b7280',
              textAlign: 'center',
            }}
          >
            © {new Date().getFullYear()} Plastic Analytics. All rights reserved.
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
    </div>
  );
};

export default ChatSupportWidget;